import { useQuery } from '@tanstack/react-query'
import { useStoreMap, useUnit } from 'effector-react'
import _ from 'lodash'
import { Batcher } from 'promise-batcher'

import { isFeatureEnabled } from '@/shared/config'
import type { Language } from '@/shared/lib/i18n'

import type * as chatapi from '../api'
import * as chatModel from '../model'
import { useChannelMessage } from './use-channel-message'

export function useMessageTranslation(messageId: string | undefined, lang: string): chatapi.MessageTranslation | null {
    useAutoTranslate(messageId, lang)

    const translation = useTranslation(messageId, lang)
    const shown = useMessageTranslationShowState(messageId, lang)

    return shown ? translation : null
}

export function useMessageReplyTranslation(
    messageId: string | undefined,
    lang: string
): chatapi.MessageTranslation | null {
    const message = useChannelMessage(messageId)

    const replyTranslation = useTranslation(message?.reply_message_id, lang)
    const messageTranslationShown = useMessageTranslationShowState(messageId, lang)

    // show reply translation only if message translation is shown
    return messageTranslationShown ? replyTranslation : null
}

function useMessageTranslationShowState(messageId: string | undefined, lang: string): boolean {
    return useStoreMap({
        store: chatModel.$messageTranslationShowState,
        keys: [messageId, lang],
        fn: (showState, [messageId, lang]) => (messageId ? showState[messageId]?.[lang] : false) ?? false,
    })
}

function useTranslation(messageId: string | undefined, lang: string) {
    return useStoreMap({
        store: chatModel.$messageTranslation,
        keys: [messageId, lang],
        fn: (translation, [messageId, lang]) => (messageId ? translation[messageId]?.[lang] : null) ?? null,
    })
}

// automatically translate message if it's not translated yet
function useAutoTranslate(messageId: string | undefined, lang: string) {
    const channel = useUnit(chatModel.$chatChannel)
    const message = useChannelMessage(messageId)
    const translation = useTranslation(messageId, lang)

    const isAutoTranslateEnabled = useUnit(chatModel.$channelAutoTranslate)

    // translate if:
    const enabled =
        // auto translate feature is enabled
        isFeatureEnabled('chat-auto-translate') &&
        // message id is available
        Boolean(messageId) &&
        // channel is in auto-translate allowed list
        channel === 'public:global' &&
        // auto translate toggle is enabled
        isAutoTranslateEnabled &&
        // message is not translated yet
        !translation &&
        // message is not optimistic
        message !== null &&
        message.optimistic_id === undefined

    useQuery({
        queryKey: ['message-translation', messageId, lang],
        queryFn: () => translationBatcher.getResult({ messageId: messageId!, lang }),
        enabled: enabled,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
    })
}

// batcher for message translation requests
const translationBatcher = new Batcher<{ messageId: string; lang: string }, null>({
    maxBatchSize: 200,
    queuingDelay: 1000,
    batchingFunction: (requests) => {
        const byLang = _.groupBy(requests, (r) => r.lang)
        for (const [lang, requests] of Object.entries(byLang)) {
            chatModel.messageTranslateMany({
                target_lang: lang as Language,
                message_id_list: requests.map((r) => r.messageId),
            })
        }

        // the request and response arrays must be of equal length
        return requests.map(() => null)
    },
})
